import React, { useState } from 'react';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress
} from '@mui/material';
import { DigitalFlyer } from '../../../services/digitalFlyersApi';
import { useAllFlyers, useApproveFlyer, useRejectFlyer } from 'src/queries/flyers';

const FlyerAdminPage: React.FC = () => {
  const [selectedFlyer, setSelectedFlyer] = useState<DigitalFlyer | null>(null);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectionMessage, setRejectionMessage] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);

  const { data: flyers = [], isPending, isError } = useAllFlyers('AWAITING_APPROVAL');
  const approveFlyer = useApproveFlyer();
  const rejectFlyer = useRejectFlyer();

  // Approving or rejecting removes the flyer from the pending queue, which the
  // mutations express by invalidating the list rather than splicing it locally.
  const isMutating = approveFlyer.isPending || rejectFlyer.isPending;
  const error = isError
    ? 'Failed to load pending flyers. Please try again later.'
    : actionError;

  const handleApprove = async (flyer: DigitalFlyer) => {
    setActionError(null);
    try {
      await approveFlyer.mutateAsync({ flyerId: flyer.id });
    } catch (err) {
      setActionError('Failed to approve flyer. Please try again.');
      console.error('Error approving flyer:', err);
    }
  };

  const handleReject = async () => {
    if (!selectedFlyer || !rejectionMessage.trim()) return;

    setActionError(null);
    try {
      await rejectFlyer.mutateAsync({
        flyerId: selectedFlyer.id,
        rejectionMessage,
      });
      setShowRejectDialog(false);
      setSelectedFlyer(null);
      setRejectionMessage('');
    } catch (err) {
      setActionError('Failed to reject flyer. Please try again.');
      console.error('Error rejecting flyer:', err);
    }
  };

  if (isPending) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Pending Flyers
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Merchant</TableCell>
                <TableCell>Message</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Cost (ZAR)</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {flyers.map((flyer) => (
                <TableRow key={flyer.id}>
                  <TableCell>{flyer.id}</TableCell>
                  <TableCell>{flyer.merchant.name}</TableCell>
                  <TableCell>{flyer.message}</TableCell>
                  <TableCell>
                    {new Date(flyer.date_created).toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric'
                    })}
                  </TableCell>
                  <TableCell>R {flyer.cost.toFixed(2)}</TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      onClick={() => handleApprove(flyer)}
                      disabled={isMutating}
                      sx={{ mr: 1 }}
                    >
                      Approve
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      color="error"
                      onClick={() => {
                        setSelectedFlyer(flyer);
                        setShowRejectDialog(true);
                      }}
                      disabled={isMutating}
                    >
                      Reject
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {flyers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body1" color="text.secondary">
                      No pending flyers to review
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Reject Dialog */}
      <Dialog 
        open={showRejectDialog} 
        onClose={() => {
          setShowRejectDialog(false);
          setSelectedFlyer(null);
          setRejectionMessage('');
        }}
      >
        <DialogTitle>Reject Flyer</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom sx={{ mt: 2 }}>
            Please provide feedback for the merchant about why this flyer was rejected:
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            label="Rejection Message"
            value={rejectionMessage}
            onChange={(e) => setRejectionMessage(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setShowRejectDialog(false);
              setSelectedFlyer(null);
              setRejectionMessage('');
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleReject}
            variant="contained" 
            color="error"
            disabled={isMutating || !rejectionMessage.trim()}
          >
            {isMutating ? <CircularProgress size={24} /> : 'Reject Flyer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default FlyerAdminPage; 